import _ from 'lodash';
import { ISecurityProfile } from '../../api/vault/securityProfiles';

/**
 * VV-315: keeping an asset's security profiles that its Security field can't show.
 *
 * The field works in profile names, and offers only the profiles in `productOptions` (the
 * current product's orgs and groups). An id it can't show - one the API hides from this user,
 * such as the Film Room group for anyone but a super admin, or a named profile the field
 * doesn't offer - has no chip, so the user can't pick it, and rebuilding the list from the
 * names picked would drop it. These helpers keep such ids on save instead.
 */

// The profile names the Security field can show: the labels of the options it offers.
export const selectableProfileNames = (productOptions: Array<{ options: Array<{ label: string }> }> = []): Set<string> => (
  new Set(productOptions.flatMap((group) => group.options.map((option) => option.label)))
);

// The ids on an asset that the field can't show. With no options loaded yet, that is every id,
// which keeps them all rather than risk dropping one.
export const unshownSecurityProfileIds = (
  ids: string[] | null | undefined,
  profiles: ISecurityProfile[],
  shownNames: Set<string>,
): string[] => (ids ?? []).filter((id) => {
  const name = profiles.find((sp) => sp._id === id)?.name;
  return !name || !shownNames.has(name);
});

// The list to save: the ids of the names picked, then the ids kept, each once - the profile
// list can hold a profile twice after an update, which would otherwise repeat its id.
export const securityProfileUpdate = (
  pickedNames: string[],
  profiles: ISecurityProfile[],
  keepIds: string[],
): string[] => _.uniq([
  ...profiles.filter((sp) => pickedNames.includes(sp.name)).map((sp) => sp._id),
  ...keepIds,
]);
